import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from '@/components/ui/sidebar';
import { WIZARD_STEPS } from './wizard/types';
import {
  LayoutDashboard,
  Shield,
  LogOut,
  FileText,
  DollarSign,
  Briefcase,
  Home,
  Receipt,
  CreditCard,
  AlertTriangle,
  TrendingUp,
  CheckCircle,
  Check,
  Building2,
  Users,
  Settings,
  Calculator,
  BarChart3,
  HelpCircle,
  FileEdit,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import SupportForm from '@/components/SupportForm';
import { useMySupportTickets } from '@/hooks/useMySupportTickets';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { hidesPersonalClients } from '@/lib/clientBook';
import { Separator } from '@/components/ui/separator';
import { Network } from 'lucide-react';
import tacticalLogo from '@/assets/tactical-tax-logo.png';

export type AppView =
  | 'wizard'
  | 'dashboard'
  | 'drafts'
  | 'client-view'
  | 'compare'
  | 'results'
  | 'team'
  | 'team-management'
  | 'org-scenarios'
  | 'assignments'
  | 'settings';

interface AppSidebarProps {
  currentView: AppView;
  wizardStep: number;
  visitedSteps: Set<number>;
  onViewChange: (view: AppView) => void;
  onWizardStepChange: (step: number) => void;
  onNewScenario: () => void;
  scenarioCount: number;
  /** Count of in-progress drafts. When > 0, a "Drafts" nav item appears under My Clients. */
  draftCount?: number;
  onSaveAndCalculate?: () => void;
  /** Renders a "Save & Compare" button below Save & Calculate. Only set when
   * editing a baseline that has existing strategies — clicking saves and routes
   * straight to the comparison view. */
  onSaveAndCompare?: () => void;
}

const STEP_ICONS = [
  FileText,
  DollarSign,
  Briefcase,
  Home,
  Receipt,
  CreditCard,
  AlertTriangle,
  TrendingUp,
  CheckCircle,
];

export default function AppSidebar({
  currentView,
  wizardStep,
  visitedSteps,
  onViewChange,
  onWizardStepChange,
  onNewScenario,
  scenarioCount,
  draftCount = 0,
  onSaveAndCalculate,
  onSaveAndCompare,
}: AppSidebarProps) {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const { profile, user, isSuperAdmin, isPlatformAdmin, isOrgOwner, isOrgAdmin, org, isSoloOrg, isPlatformStaffOrg, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [supportOpen, setSupportOpen] = useState(false);
  const { unread: unreadSupportReplies } = useMySupportTickets();

  // One toast per new support message per browser session, with a way in.
  // Keyed on the ticket AND when support last wrote, so a second reply on the
  // same conversation toasts again.
  const replyKey = (t: { id: string; last_support_message_at: string | null }) => `${t.id}@${t.last_support_message_at}`;
  useEffect(() => {
    let shown: string[] = [];
    try { shown = JSON.parse(sessionStorage.getItem('support-replies-toasted') ?? '[]'); } catch { /* storage blocked */ }
    const fresh = unreadSupportReplies.filter(t => !shown.includes(replyKey(t)));
    if (fresh.length === 0) return;
    for (const t of fresh) {
      toast.info(`Support replied: ${t.subject}`, {
        id: `support-reply-${t.id}`,
        action: { label: 'View', onClick: () => setSupportOpen(true) },
      });
    }
    try {
      sessionStorage.setItem('support-replies-toasted', JSON.stringify([...shown, ...fresh.map(replyKey)]));
    } catch { /* storage blocked */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadSupportReplies.map(replyKey).join(',')]);

  // Three sidebar shapes (mutually exclusive for non-super-admin users):
  //   1. Solo owner         — the paying customer with no team yet.
  //   2. Multi-member owner — owner/admin once the account has at least 2 members.
  //   3. Agent              — non-owner, non-admin invited team members.
  const isSoloOwner = !isSuperAdmin && isOrgOwner && isSoloOrg;
  const isMultiMemberOwner = !isSuperAdmin && (isOrgOwner || isOrgAdmin) && !isSoloOrg;
  const isAgentUser = !isSuperAdmin && !isOrgOwner && !isOrgAdmin;
  const showWorkspace = !isSuperAdmin && (isSoloOwner || isMultiMemberOwner || isAgentUser);
  // A support admin (Dena, Veronica) carries no client book: they build work
  // for Andrew and Dylan, so My Clients is always empty and the New Client
  // button on it would file the client under the wrong person. Hide those two
  // items; Settings stays, because every role reaches Settings.
  const noClientBook = hidesPersonalClients({
    hasClientBook: profile?.has_client_book,
    isSuperAdmin,
    isOrgOwner,
    isOrgAdmin,
  });
  // Account lane (Team + All Clients) is the inner-circle workspace, exclusive to
  // orgs owned by platform staff (super_admin/platform_admin) — everyone else's
  // team surface lives in Team Management instead.
  const showAccountSection = !isSuperAdmin && (isSoloOwner || isMultiMemberOwner) && !!org && isPlatformStaffOrg;

  const navItem = (view: AppView, icon: React.ReactNode, label: string, badge?: string | number, dataTour?: string) => (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => onViewChange(view)}
        className={currentView === view ? 'bg-primary/10 text-primary font-semibold' : ''}
        data-tour={dataTour}
        tooltip={label}
      >
        {icon}
        {!collapsed && <span>{label}</span>}
        {!collapsed && badge !== undefined && (
          <span className="ml-auto text-xs font-mono text-muted-foreground">{badge}</span>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          <img
            src={tacticalLogo}
            alt="Tactical Tax Genie"
            className="h-9 w-9 shrink-0 object-contain"
            style={{ borderRadius: 0 }}
          />
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-bold tracking-tight truncate" style={{ color: '#2D4B6E' }}>
                Tactical Tax Genie
              </p>
              {org && (
                <p className="text-[10px] text-muted-foreground truncate flex items-center gap-1">
                  <Building2 className="h-2.5 w-2.5 shrink-0" />
                  {org.name}
                </p>
              )}
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        {currentView === 'wizard' && (
          <SidebarGroup data-tour="wizard-steps">
            <SidebarGroupLabel>Workflow</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {WIZARD_STEPS.map((step, i) => {
                  const StepIcon = STEP_ICONS[i];
                  const isActive = i === wizardStep;
                  const isCompleted = visitedSteps.has(i);

                  return (
                    <SidebarMenuItem key={step.id}>
                      <SidebarMenuButton
                        onClick={() => onWizardStepChange(i)}
                        tooltip={step.shortLabel}
                        className={
                          isActive
                            ? 'bg-primary/10 text-primary font-semibold border-l-2 border-primary'
                            : 'text-muted-foreground'
                        }
                      >
                        {isCompleted ? (
                          <div className="h-4 w-4 rounded-full bg-primary flex items-center justify-center shrink-0">
                            <Check className="h-2.5 w-2.5 text-primary-foreground" />
                          </div>
                        ) : collapsed ? (
                          <StepIcon className="h-4 w-4 shrink-0" />
                        ) : (
                          <div
                            className={`h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 text-[9px] font-bold ${
                              isActive
                                ? 'border-primary text-primary'
                                : 'border-muted-foreground/50 text-muted-foreground'
                            }`}
                          >
                            {i + 1}
                          </div>
                        )}
                        {!collapsed && <span className="text-sm">{step.shortLabel}</span>}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {currentView === 'wizard' && onSaveAndCalculate && (
          <div className="px-3 pb-2 space-y-2">
            <Button size="sm" className="w-full gap-2" onClick={onSaveAndCalculate} data-tour="save-calculate">
              <Calculator className="h-4 w-4 shrink-0" />
              {!collapsed && <span>Save & Calculate</span>}
            </Button>
            {onSaveAndCompare && (
              <Button
                size="sm"
                onClick={onSaveAndCompare}
                className="w-full gap-2 bg-success text-success-foreground hover:bg-success/90"
                data-tour="save-compare"
              >
                <BarChart3 className="h-4 w-4 shrink-0" />
                {!collapsed && <span>Save & Compare</span>}
              </Button>
            )}
          </div>
        )}

        <Separator className="mx-4 w-auto" />

        {showWorkspace && !noClientBook && (
          <SidebarGroup>
            <SidebarGroupLabel>My Work</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {!noClientBook && navItem(
                  'dashboard',
                  <LayoutDashboard className="h-4 w-4" />,
                  'My Clients',
                  undefined,
                  'nav-my-clients',
                )}
                {!noClientBook && draftCount > 0 && navItem(
                  'drafts',
                  <FileEdit className="h-4 w-4" />,
                  'Drafts',
                  draftCount,
                  'nav-drafts',
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {showAccountSection && (
          <>
            <Separator className="mx-4 w-auto" />
            <SidebarGroup>
              <SidebarGroupLabel>Organization</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {/* Team is platform-staff-and-owner only (Drew, 2026-09-22).
                      A plain org_admin here — Dena, Veronica — has no business
                      in the member list: they support the advisors, they don't
                      run the org. Deliberately gating THIS ITEM and not the
                      whole Account lane, because All Clients lives in the same
                      lane and is the only clients surface a support admin has
                      once their client book is off. Hiding the lane would
                      strand them with nowhere to work. */}
                  {(isPlatformAdmin || isOrgOwner) &&
                    navItem('team', <Users className="h-4 w-4" />, 'Team', undefined, 'nav-team')}
                  {isMultiMemberOwner &&
                    navItem('org-scenarios', <FileText className="h-4 w-4" />, 'All Clients', undefined, 'nav-all-clients')}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {/* Team Management is live-data now: everyone who manages a downline
            gets it (scoped to their own branch by the server). Agents never
            see it. An org owner/admin whose org is platform-staff-owned lives
            in the Account lane instead — their hierarchy tree renders empty by
            design, so hide the nav rather than show a dead end. Platform admins
            keep it regardless (they manage the tree itself). */}
        {(isSuperAdmin || isPlatformAdmin || ((isOrgOwner || isOrgAdmin) && !isPlatformStaffOrg)) && (
          <>
            <Separator className="mx-4 w-auto" />
            <SidebarGroup>
              <SidebarGroupLabel>Hierarchy</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => navigate('/team-management')}
                      className={location.pathname === '/team-management' ? 'bg-primary/10 text-primary font-semibold' : ''}
                      data-tour="nav-downline"
                      tooltip="Downline Management"
                    >
                      <Network className="h-4 w-4" />
                      {!collapsed && <span>Downline Management</span>}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {(isSuperAdmin || isPlatformAdmin) && (
          <>
            <Separator className="mx-4 w-auto" />
            <SidebarGroup>
              <SidebarGroupLabel>{isSuperAdmin ? 'Admin' : 'Platform'}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton onClick={() => navigate('/super-admin')} tooltip="Admin Panel">
                      <Shield className="h-4 w-4" />
                      {!collapsed && <span>Admin Panel</span>}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      <SidebarFooter className="p-3">
        {!collapsed && (
          <p className="text-xs text-muted-foreground truncate px-2 mb-1">
            {profile?.full_name || user?.email || 'Unknown'}
          </p>
        )}
        <SidebarMenu>
          {navItem('settings', <Settings className="h-4 w-4" />, 'Settings', undefined, 'nav-settings')}
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => setSupportOpen(true)} tooltip="Contact Support">
              <span className="relative">
                <HelpCircle className="h-4 w-4" />
                {unreadSupportReplies.length > 0 && (
                  <span
                    className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary"
                    aria-label="New reply from support"
                    data-testid="support-reply-dot"
                  />
                )}
              </span>
              {!collapsed && <span>Contact Support</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={signOut} tooltip="Sign Out">
              <LogOut className="h-4 w-4" />
              {!collapsed && <span>Sign Out</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SupportForm open={supportOpen} onOpenChange={setSupportOpen} />
      </SidebarFooter>
    </Sidebar>
  );
}
